<script setup lang="ts">
import { Drawer, DrawerContent, DrawerTitle } from "~/components/ui/drawer";

const appConfig = useAppConfig();
const localePath = useLocalePath();
const isMobileMenuOpen = ref(false);
const businessStore = useBusinessStore();
const { t, localeProperties } = useI18n();

businessStore.$reset();

appConfig.ui.primary = "default";

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

// vaul's direction is physical (left/right), unlike CSS logical start/end.
const drawerDirection = computed(() =>
  localeProperties.value.dir === "rtl" ? "right" : "left",
);

const header = ref(null);

const calcHeaderHeight = computed(() => {
  if (header.value) {
    return header.value?.offsetHeight;
  }
  return 0;
});
</script>

<template>
  <div class="min-h-screen lg:h-screen">
    <div class="flex flex-col bg-white h-full">
      <!-- Header -->
      <div
        ref="header"
        class="w-full bg-white px-4 sm:px-6 py-4 fixed lg:relative top-0 lg:top-auto z-[25] border-b border-gray-100 flex items-center justify-between gap-4"
      >
        <div class="flex items-center gap-3">
          <!-- Mobile Menu Button -->
          <UButton
            icon="i-approved-menu"
            variant="ghost"
            color="gray"
            class="lg:hidden"
            @click="isMobileMenuOpen = true"
          />

          <div class="flex-shrink-0">
            <ULink :to="localePath('/')">
              <img src="/h-logo.svg" alt="bookforme" class="w-43 xs:w-auto">
            </ULink>
          </div>
        </div>

        <!-- Search Bar (Hidden on mobile) -->
        <!-- <div class="hidden md:flex flex-1 justify-center">
          <UInput
            :placeholder="$t('placeholder.account_search')"
            size="xl"
            icon="i-approved-search"
            variant="outline"
            :ui="{
              base: 'w-[400px] lg:w-[500px] relative block disabled:cursor-not-allowed disabled:opacity-75 focus:outline-none border-0',
              rounded: 'rounded-full',
              padding: { xl: 'px-3.5 py-2 lg:py-3' },
            }"
          />
        </div> -->

        <!-- Actions -->
        <div class="flex items-center gap-2 sm:gap-2.5 flex-shrink-0">
          <CommonLanguagesMenu
            class="rounded-full w-9 h-9 sm:w-10 sm:h-10 lg:w-11 lg:h-11"
          />
          <CommonClientDropdown />
        </div>
      </div>

      <!-- Mobile Menu Drawer -->
      <Drawer v-model:open="isMobileMenuOpen" :direction="drawerDirection">
        <DrawerContent
          class="overflow-hidden border-e border-gray-100 bg-white rounded-e-2xl md:rounded-e-3xl lg:rounded-e-3xl data-[vaul-drawer-direction=left]:w-[85%] data-[vaul-drawer-direction=left]:sm:max-w-none data-[vaul-drawer-direction=left]:md:w-1/2 data-[vaul-drawer-direction=left]:lg:w-[calc(100%-200px)] data-[vaul-drawer-direction=right]:w-[85%] data-[vaul-drawer-direction=right]:sm:max-w-none data-[vaul-drawer-direction=right]:md:w-1/2 data-[vaul-drawer-direction=right]:lg:w-[calc(100%-200px)]"
        >
          <DrawerTitle class="sr-only">{{ t("page.account_manager.title") }}</DrawerTitle>
          <div class="flex flex-col h-full min-h-0 divide-y divide-gray-100">
            <div class="shrink-0 flex items-center gap-x-1 p-5">
              <UButton
                icon="i-approved-arrow-left"
                size="xl"
                class="rounded-xl"
                color="gray"
                variant="ghost"
                :ui="{ square: { xl: 'p-2' } }"
                @click="closeMobileMenu"
              />
              <h2 class="text-gray-900 text-xl font-bold capitalize">
                {{ $t("page.account_manager.title") }}
              </h2>
            </div>

            <AccountNavLinks
              orientation="vertical"
              class="!px-10 text-base flex-1 min-h-0 overflow-y-auto py-5"
              @click="closeMobileMenu"
            />

            <div class="shrink-0 flex flex-col gap-y-4 px-10 py-5">
              <div class="flex flex-col">
                <span class="text-sm text-gray-500 font-medium"
                  >{{ $t("powered_by") }}
                  <NuxtLink :to="localePath('/')" class="text-primary underline"
                    >bookforme.com</NuxtLink
                  ></span
                >
                <span class="text-sm text-gray-500 font-medium">{{
                  $t("all_rights_reserved", {
                    year: `© ${new Date().getFullYear()}`,
                  })
                }}</span>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <!-- Main -->
      <div
        :style="{ marginTop: `${calcHeaderHeight}px` }"
        class="flex-1 flex flex-col px-4 sm:px-6 md:px-8 lg:px-10 xl:px-50 py-6 sm:py-8 md:py-10 lg:py-12 xl:py-15 lg:!mt-0 h-full min-h-0 overflow-y-auto"
      >
        <div
          class="text-center text-deep-indigo mb-6 sm:mb-7 md:mb-8 lg:mb-9 xl:mb-10"
        >
          <h1
            class="font-black text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-[2.2rem] leading-tight"
          >
            {{ $t("page.account_manager.title") }}
          </h1>
          <h2
            class="text-sm sm:text-base md:text-base lg:text-lg font-medium mt-1 sm:mt-1.5 md:mt-2"
          >
            {{ $t("page.account_manager.desc") }}
          </h2>
        </div>
        <div class="max-w-full lg:mx-auto">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>
